export interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer_source: ReferrerSource;
  referrer_host: string;
  landing_path: string;
  captured_at: string;
}

export type ReferrerSource =
  | "direct"
  | "reddit"
  | "google"
  | "bing"
  | "duckduckgo"
  | "chatgpt"
  | "claude"
  | "perplexity"
  | "twitter"
  | "linkedin"
  | "youtube"
  | "facebook"
  | "instagram"
  | "github"
  | "hackernews"
  | "producthunt"
  | "referral"
  | "unknown";

export const REFERRER_PATTERNS: ReadonlyArray<{
  test: RegExp;
  source: ReferrerSource;
}> = [
  { test: /^(.*\.)?reddit\.com$/, source: "reddit" },
  { test: /^old\.reddit\.com$/, source: "reddit" },
  { test: /^redd\.it$/, source: "reddit" },
  { test: /^(www\.)?google\.com$/, source: "google" },
  { test: /^(www\.)?google\.[a-z.]+$/, source: "google" },
  { test: /^chat\.openai\.com$/, source: "chatgpt" },
  { test: /^chatgpt\.com$/, source: "chatgpt" },
  { test: /^claude\.ai$/, source: "claude" },
  { test: /^perplexity\.ai$/, source: "perplexity" },
  { test: /^twitter\.com$/, source: "twitter" },
  { test: /^x\.com$/, source: "twitter" },
  { test: /^t\.co$/, source: "twitter" },
  { test: /^(www\.)?linkedin\.com$/, source: "linkedin" },
  { test: /^lnkd\.in$/, source: "linkedin" },
  { test: /^(www\.)?youtube\.com$/, source: "youtube" },
  { test: /^youtu\.be$/, source: "youtube" },
  { test: /^(www\.)?facebook\.com$/, source: "facebook" },
  { test: /^fb\.com$/, source: "facebook" },
  { test: /^fb\.me$/, source: "facebook" },
  { test: /^(www\.)?instagram\.com$/, source: "instagram" },
  { test: /^github\.com$/, source: "github" },
  { test: /^news\.ycombinator\.com$/, source: "hackernews" },
  { test: /^www\.producthunt\.com$/, source: "producthunt" },
  { test: /^(www\.)?bing\.com$/, source: "bing" },
  { test: /^duckduckgo\.com$/, source: "duckduckgo" },
];

const STORAGE_KEY = "snowpad_attribution";

function normalizeReferrer(referrer: string): {
  source: ReferrerSource;
  host: string;
} {
  if (!referrer) return { source: "direct", host: "" };

  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return { source: "unknown", host: "" };
  }

  let host = url.hostname.toLowerCase();
  if (host.startsWith("www.")) host = host.slice(4);

  if (typeof window !== "undefined" && host === window.location.host) {
    return { source: "direct", host: "" };
  }

  for (const { test, source } of REFERRER_PATTERNS) {
    if (test.test(host)) return { source, host };
  }

  return { source: "referral", host };
}

export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Attribution;
    if (typeof parsed !== "object" || parsed === null) return null;
    if (typeof parsed.referrer_source !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

function readUtm(searchParams: URLSearchParams, key: string): string | null {
  const v = searchParams.get(key);
  if (!v) return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function captureAttribution(): Attribution {
  if (typeof window === "undefined") {
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      referrer_source: "direct",
      referrer_host: "",
      landing_path: "",
      captured_at: new Date().toISOString(),
    };
  }

  const existing = getAttribution();
  if (existing) return existing;

  const searchParams = new URL(window.location.href).searchParams;
  const { source: refSource, host: refHost } = normalizeReferrer(
    document.referrer,
  );

  const attribution: Attribution = {
    utm_source: readUtm(searchParams, "utm_source"),
    utm_medium: readUtm(searchParams, "utm_medium"),
    utm_campaign: readUtm(searchParams, "utm_campaign"),
    utm_content: readUtm(searchParams, "utm_content"),
    utm_term: readUtm(searchParams, "utm_term"),
    referrer_source: refSource,
    referrer_host: refHost,
    landing_path: window.location.pathname,
    captured_at: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
  }

  return attribution;
}

export function attributionToParams(
  a: Attribution | null,
): Record<string, string> {
  if (!a) return {};
  const out: Record<string, string> = {};
  if (a.utm_source) out.utm_source = a.utm_source;
  if (a.utm_medium) out.utm_medium = a.utm_medium;
  if (a.utm_campaign) out.utm_campaign = a.utm_campaign;
  if (a.utm_content) out.utm_content = a.utm_content;
  if (a.utm_term) out.utm_term = a.utm_term;
  out.referrer_source = a.referrer_source;
  if (a.referrer_host) out.referrer_host = a.referrer_host;
  if (a.landing_path) out.landing_path = a.landing_path;
  return out;
}
